import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { theme } from './theme.js';
import { css, EASE_SPRING } from './styles.js';
import { Spinner } from './Spinner.js';
import { useDensity, getDensityConfig } from './DensityProvider.js';
// C-03 fix: dark text on bright backgrounds for WCAG contrast
const VARIANT_STYLES = {
    primary: {
        base: {
            background: 'linear-gradient(135deg, var(--k-brand) 0%, var(--k-brand-dark) 100%)',
            color: 'var(--k-brand-text)',
            border: 'none',
            boxShadow: '0 0 20px var(--k-brand-glow)',
        },
        hover: {
            boxShadow: '0 0 30px var(--k-brand-glow-strong)',
            transform: 'translateY(-1px)',
        },
    },
    secondary: {
        base: {
            background: 'transparent',
            color: 'var(--k-accent)',
            border: '1.5px solid var(--k-accent)',
        },
        hover: {
            // `background`, not `backgroundColor`. base and hover are spread into one style
            // object, so hovering added backgroundColor ALONGSIDE the base's background — and
            // un-hovering removed it again while the shorthand still stood. That is exactly
            // React's "Removing a style property during rerender (backgroundColor) when a
            // conflicting property is set (background)", and it fired on every button in the
            // product, which is why it clustered on pages with a lot of them.
            //
            // Both states must set the SAME property. A plain colour through the shorthand
            // renders identically — background-image is none either way.
            background: 'var(--k-accent-glow)',
            color: 'var(--k-accent-hover)',
        },
    },
    danger: {
        base: {
            background: 'linear-gradient(135deg, var(--k-danger) 0%, var(--k-danger-dark) 100%)',
            color: 'var(--k-danger-text)',
            border: 'none',
            boxShadow: '0 0 20px rgba(var(--k-danger-rgb), 0.15)',
        },
        hover: {
            boxShadow: '0 0 30px rgba(var(--k-danger-rgb), 0.3)',
            transform: 'translateY(-1px)',
        },
    },
    success: {
        base: {
            background: 'linear-gradient(135deg, var(--k-success) 0%, var(--k-success-dark) 100%)',
            color: 'var(--k-brand-text)',
            border: 'none',
            boxShadow: '0 0 20px rgba(var(--k-success-rgb), 0.15)',
        },
        hover: {
            boxShadow: '0 0 30px rgba(var(--k-success-rgb), 0.3)',
            transform: 'translateY(-1px)',
        },
    },
    ghost: {
        base: {
            background: 'transparent',
            color: 'var(--k-text-secondary)',
            border: 'none',
        },
        hover: {
            color: 'var(--k-text-primary)',
            // See `secondary` above — base sets `background`, so hover must too.
            background: 'var(--k-bg-hover)',
        },
    },
};
export const Button = ({ children, onClick, disabled = false, isLoading = false, variant = 'primary', size = 'md', className, style, type = 'button', title, density: densityProp, ...rest }) => {
    const contextDensity = useDensity();
    const densityConfig = densityProp ? getDensityConfig(densityProp) : contextDensity;
    // C-01: State-based hover/press instead of CSS pseudo-selectors
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    const isDisabled = disabled || isLoading;
    const SIZE_MAP = {
        sm: {
            padding: `${densityConfig.gap} ${densityConfig.padding}`,
            fontSize: densityConfig.fontLabel,
            minHeight: '32px',
        },
        md: {
            padding: `${densityConfig.padding} ${densityConfig.paddingLg}`,
            fontSize: densityConfig.fontBody,
            minHeight: densityConfig.rowHeight,
        },
        lg: {
            padding: `${densityConfig.paddingLg} ${theme.spacing.xl}`,
            fontSize: theme.typography.fontSize.lg.size,
            minHeight: '52px',
        },
    };
    const variantStyle = VARIANT_STYLES[variant];
    const buttonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: densityConfig.gap,
        borderRadius: densityConfig.radius,
        fontWeight: theme.typography.fontWeight.semibold,
        fontFamily: theme.typography.fontFamily.primary,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: `all 200ms ${EASE_SPRING}`,
        opacity: isDisabled ? 0.5 : 1,
        outline: 'none',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        position: 'relative',
        ...SIZE_MAP[size],
        ...variantStyle.base,
        ...(hovered && !isDisabled ? variantStyle.hover : {}),
        ...(pressed && !isDisabled
            ? { transform: 'scale(0.97)', transition: 'transform 80ms ease-in' }
            : {}),
    };
    return (_jsx("button", { type: type, onClick: onClick, disabled: isDisabled, title: title, style: css(buttonStyle, style), className: `k-focus-ring ${className || ''}`, onMouseEnter: () => setHovered(true), onMouseLeave: () => {
            setHovered(false);
            setPressed(false);
        }, onMouseDown: () => !isDisabled && setPressed(true), onMouseUp: () => setPressed(false), "aria-busy": isLoading, ...rest, children: isLoading ? (_jsxs(_Fragment, { children: [_jsx(Spinner, { size: "sm" }), children] })) : (children) }));
};
//# sourceMappingURL=Button.js.map